<script>
  import { classnames } from '../../utils/classnames';

  /**
   * Background color of the icon.
   * @type {string}
   * @default undefined
   */
  export let background = undefined;

  /**
   * If icon has a round outline.
   * @type {boolean}
   * @default false
   */
  export let border = false;

  /**
   * Color of the icon.
   * @type {string}
   * @default undefined
   */
  export let color = undefined;

  /**
   * Additional CSS class name for the component
   * @type {string}
   * @default ''
   */
  let className = '';
  export { className as class };

  /**
   * A caller-supplied inline `style` string is accepted (via `$$restProps`) and
   * merged with the `size`-driven `font-size` — use it for one-off values that
   * have no prop/utility (e.g. `style="left: 8px"`, a custom color token).
   * @type {string}
   * @default undefined
   */

  /**
   * Name of the Lucide icon to display (kebab-case, e.g. `camera`, `x-circle`).
   * Rendered via the Lucide icon font, so the value maps to the `icon-<name>`
   * glyph class. Alias names (e.g. `x-circle` for `circle-x`) are supported.
   * @see https://lucide.dev/icons/
   * @type {string}
   * @default ''
   */
  export let name = '';

  /**
   * ARIA role of the icon to display.
   * @type {string}
   * @default 'presentation'
   */
  export let role = 'presentation';

  /**
   * Pixel size of the icon, applied as `font-size`. When omitted, the icon
   * inherits the surrounding text's `font-size` (matching the previous icon
   * font's behaviour).
   * @type {number}
   * @default undefined
   */
  export let size = undefined;

  $: classes = classnames(className, name && `icon-${name}`, {
    border: border,
    [`border-${color}`]: border && color,
    'border-current': border && !color,
    [`bg-${background}`]: background,
    'rounded-circle p-1': border || background,
    [`text-${color}`]: color
  });

  // Merge a caller-supplied `style` with the size-driven font-size. The explicit
  // `style` attribute below overrides the `{...$$restProps}` spread, so without
  // this merge any `style` passed at a call site would be silently dropped.
  $: style =
    [size && `font-size: ${size}px`, $$restProps.style].filter(Boolean).join('; ') || undefined;
</script>

{#if name}
  <i
    {...$$restProps}
    {role}
    class={classes}
    {style}
    on:click
    on:mousedown
    on:mouseup
  />
{/if}

<style>
  i {
    display: inline-flex;
    vertical-align: middle;
    line-height: 1;
  }

  .border-current {
    border-color: currentColor !important;
  }
</style>
